import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Users,
  UserCheck,
  Building,
  Search,
  UserPlus,
  RefreshCw,
  Gift,
  Award,
  Sparkles,
  SlidersHorizontal,
} from "lucide-react";
import Notification, { showToast } from "../../components/common/notification/Notification";
import { getStoredAuthSession } from "../auth/authSession";
import { getUserTypeName } from "../../admin/navigation/adminNavigation";
import {
  fetchNetworkPartners,
  fetchContacts,
  fetchPartnerTeams,
  fetchHeaderUserDetails,
  deleteNetworkPartner,
  deleteContact,
  deletePartnerTeam,
} from "./networkApi";

import NetworkPartnersTab from "./NetworkPartnersTab";
import ContactsFriendsTab from "./ContactsFriendsTab";
import PartnerTeamsTab from "./PartnerTeamsTab";
import AddPartnerModal from "./AddPartnerModal";
import EditPartnerModal from "./EditPartnerModal";
import PartnerDetailsDrawer from "./PartnerDetailsDrawer";
import AddContactModal from "./AddContactModal";
import EditContactModal from "./EditContactModal";
import ContactDetailsDrawer from "./ContactDetailsDrawer";
import CreateTeamModal from "./CreateTeamModal";
import TeamDetailsDrawer from "./TeamDetailsDrawer";
import AddTeamMemberModal from "./AddTeamMemberModal";
import "./network.css";

const NetworkHub = ({ initialTab = "partners" }) => {
  const authState = useSelector((state) => state.auth);
  const location = useLocation();
  const navigate = useNavigate();

  const session = getStoredAuthSession() || authState?.user;
  const loggedInUserId = session?.user?.id || session?.id;
  const userTypeName = String(getUserTypeName(authState) || session?.user?.userType?.name || "CUSTOMER").toUpperCase();
  const isCustomer = userTypeName === "CUSTOMER";

  // Active Tab: partners | contacts | teams
  const [activeTab, setActiveTab] = useState(() => {
    if (location.pathname.includes("friends")) return "contacts";
    if (location.pathname.includes("teams")) return "teams";
    if (location.pathname.includes("agents")) return "partners";
    return initialTab;
  });

  // Sync tab with route if path changes
  useEffect(() => {
    if (location.pathname.includes("friends")) setActiveTab("contacts");
    else if (location.pathname.includes("teams")) setActiveTab("teams");
    else if (location.pathname.includes("agents") || location.pathname.includes("network")) setActiveTab("partners");
  }, [location.pathname]);

  // Header User Details & Counts
  const [headerUser, setHeaderUser] = useState(null);

  // Data states
  const [partners, setPartners] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [teams, setTeams] = useState([]);

  // Loading states
  const [isLoadingPartners, setIsLoadingPartners] = useState(false);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [sortBy, setSortBy] = useState("DESC");

  // Modals & Drawers states
  const [showAddPartnerModal, setShowAddPartnerModal] = useState(false);
  const [showEditPartnerModal, setShowEditPartnerModal] = useState(false);
  const [selectedPartnerForEdit, setSelectedPartnerForEdit] = useState(null);
  const [showPartnerDrawer, setShowPartnerDrawer] = useState(false);
  const [selectedPartnerForDrawer, setSelectedPartnerForDrawer] = useState(null);

  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [selectedContactForEdit, setSelectedContactForEdit] = useState(null);
  const [showContactDrawer, setShowContactDrawer] = useState(false);
  const [selectedContactForDrawer, setSelectedContactForDrawer] = useState(null);

  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [showTeamDrawer, setShowTeamDrawer] = useState(false);
  const [selectedTeamForDrawer, setSelectedTeamForDrawer] = useState(null);
  const [showAddTeamMemberModal, setShowAddTeamMemberModal] = useState(false);
  const [selectedTeamForMemberAdd, setSelectedTeamForMemberAdd] = useState(null);

  // 1. Fetch Header Info
  useEffect(() => {
    if (loggedInUserId) {
      fetchHeaderUserDetails(loggedInUserId)
        .then((res) => {
          if (res?.user) setHeaderUser(res.user);
        })
        .catch((err) => console.log("Header user info fetch notice:", err));
    }
  }, [loggedInUserId]);

  // 2. Fetch Partners
  const loadPartners = useCallback(async () => {
    try {
      setIsLoadingPartners(true);
      const res = await fetchNetworkPartners({
        page_number: 1,
        page_size: 100,
        order_by: sortBy,
        isCustomer,
      });
      setPartners(res.data || []);
    } catch (err) {
      console.error("Load partners error:", err);
    } finally {
      setIsLoadingPartners(false);
    }
  }, [isCustomer, sortBy]);

  // 3. Fetch Contacts / Friends
  const loadContacts = useCallback(async () => {
    try {
      setIsLoadingContacts(true);
      const res = await fetchContacts({
        page_number: 1,
        page_size: 100,
        order_by: sortBy,
        isCustomer,
      });
      setContacts(res.data || []);
    } catch (err) {
      console.error("Load contacts error:", err);
    } finally {
      setIsLoadingContacts(false);
    }
  }, [isCustomer, sortBy]);

  // 4. Fetch Teams
  const loadTeams = useCallback(async () => {
    try {
      setIsLoadingTeams(true);
      const res = await fetchPartnerTeams({ page: 1, size: 100 });
      setTeams(res.data || []);
    } catch (err) {
      console.error("Load teams error:", err);
    } finally {
      setIsLoadingTeams(false);
    }
  }, []);

  // Initial loads
  useEffect(() => {
    loadPartners();
    loadContacts();
    if (!isCustomer) {
      loadTeams();
    }
  }, [loadPartners, loadContacts, loadTeams, isCustomer]);

  // Delete Handlers
  const handleDeletePartner = async (partner) => {
    const pName = `${partner.first_name || ""} ${partner.last_name || ""}`.trim() || "partner";
    if (!window.confirm(`Are you sure you want to remove ${pName} from your network?`)) {
      return;
    }
    try {
      const res = await deleteNetworkPartner(partner.id);
      if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
        showToast("Partner removed from network", { type: "success" });
        loadPartners();
      } else {
        showToast(res?.text || "Failed to remove partner", { type: "error" });
      }
    } catch (err) {
      console.error("Delete partner error:", err);
      showToast("Error deleting partner", { type: "error" });
    }
  };

  const handleDeleteContact = async (contact) => {
    const cName = `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || "contact";
    if (!window.confirm(`Are you sure you want to delete ${cName}?`)) {
      return;
    }
    try {
      const res = await deleteContact(contact.id);
      if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
        showToast("Contact deleted", { type: "success" });
        loadContacts();
      } else {
        showToast(res?.text || "Failed to delete contact", { type: "error" });
      }
    } catch (err) {
      console.error("Delete contact error:", err);
      showToast("Error deleting contact", { type: "error" });
    }
  };

  const handleDeleteTeam = async (team) => {
    if (!window.confirm(`Are you sure you want to delete the team "${team.name}"?`)) {
      return;
    }
    try {
      const res = await deletePartnerTeam(team.id);
      if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
        showToast("Team deleted successfully", { type: "success" });
        loadTeams();
        if (showTeamDrawer) setShowTeamDrawer(false);
      } else {
        showToast(res?.text || "Failed to delete team", { type: "error" });
      }
    } catch (err) {
      console.error("Delete team error:", err);
      showToast("Error deleting team", { type: "error" });
    }
  };

  // Filtered lists based on search and filters
  const filteredPartners = useMemo(() => {
    return partners.filter((p) => {
      const name = `${p.first_name || p.firstName || ""} ${p.last_name || p.lastName || ""}`.toLowerCase();
      const phone = (p.mobile_number || p.mobileNo || "").toLowerCase();
      const email = (p.email_address || p.emailId || "").toLowerCase();
      const city = (p.city_name || p.city || "").toLowerCase();
      const state = (p.state_name || p.state || "").toLowerCase();
      const q = searchQuery.toLowerCase().trim();

      const matchesQuery = !q || name.includes(q) || phone.includes(q) || email.includes(q) || city.includes(q);
      const matchesState = !selectedState || state.includes(selectedState.toLowerCase()) || city.includes(selectedState.toLowerCase());
      return matchesQuery && matchesState;
    });
  }, [partners, searchQuery, selectedState]);

  const filteredContacts = useMemo(() => {
    return contacts.filter((c) => {
      const name = `${c.first_name || c.firstName || ""} ${c.last_name || c.lastName || ""}`.toLowerCase();
      const phone = (c.mobile_number || c.mobileNo || "").toLowerCase();
      const email = (c.email_address || c.emailId || "").toLowerCase();
      const area = (c.area_name || c.area || "").toLowerCase();
      const q = searchQuery.toLowerCase().trim();

      return !q || name.includes(q) || phone.includes(q) || email.includes(q) || area.includes(q);
    });
  }, [contacts, searchQuery]);

  const filteredTeams = useMemo(() => {
    return teams.filter((t) => {
      const name = (t.name || "").toLowerCase();
      const desc = (t.description || "").toLowerCase();
      const q = searchQuery.toLowerCase().trim();

      return !q || name.includes(q) || desc.includes(q);
    });
  }, [teams, searchQuery]);

  // Derived user details for hero
  const currentUserName = headerUser?.first_name
    ? `${headerUser.first_name} ${headerUser.last_name || ""}`.trim()
    : session?.user?.first_name
    ? `${session.user.first_name} ${session.user.last_name || ""}`.trim()
    : "Your Network Workspace";

  const totalConnections = partners.length + contacts.length;
  const totalTeamSeats = teams.reduce((acc, t) => acc + (Number(t.total_seats) || 0), 0);

  return (
    <div className="net-hub">
      <Notification />

      {/* 1. HERO HEADER AREA */}
      <div className="net-header">
        <div className="net-header__glow" />
        <div className="net-header__content">
          <div className="net-header__user">
            <div className="net-header__avatar">
              {currentUserName.charAt(0).toUpperCase()}
            </div>
            <div className="net-header__info">
              <h1>{currentUserName}</h1>
              <div className="net-header__meta">
                <span className="net-header__badge">
                  <Sparkles size={13} /> {userTypeName} NETWORK
                </span>
                <span className="net-header__subtext">
                  Manage associate partners, clients, and agency teams in one place
                </span>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
            <button
              type="button"
              className="net-btn net-btn--secondary"
              onClick={() => {
                loadPartners();
                loadContacts();
                if (!isCustomer) loadTeams();
                showToast("Network refreshed", { type: "info" });
              }}
              title="Refresh Data"
            >
              <RefreshCw size={14} className={isLoadingPartners || isLoadingContacts ? "net-spin" : ""} />
              <span>Refresh</span>
            </button>
            <button
              type="button"
              className="net-btn net-btn--primary"
              onClick={() => setShowAddPartnerModal(true)}
            >
              <UserPlus size={16} /> Add Partner
            </button>
          </div>
        </div>
      </div>

      {/* 2. STATS ROW */}
      <div className="net-stats-grid">
        <div className="net-stat-card">
          <div className="net-stat-card__icon net-stat-card__icon--blue">
            <Users size={22} />
          </div>
          <div>
            <div className="net-stat-card__val">{partners.length}</div>
            <div className="net-stat-card__lbl">Network Partners</div>
          </div>
        </div>

        <div className="net-stat-card">
          <div className="net-stat-card__icon net-stat-card__icon--green">
            <UserCheck size={22} />
          </div>
          <div>
            <div className="net-stat-card__val">{contacts.length}</div>
            <div className="net-stat-card__lbl">Friends & Clients</div>
          </div>
        </div>

        {!isCustomer && (
          <div className="net-stat-card">
            <div className="net-stat-card__icon net-stat-card__icon--purple">
              <Building size={22} />
            </div>
            <div>
              <div className="net-stat-card__val">{teams.length}</div>
              <div className="net-stat-card__lbl">Partner Teams ({totalTeamSeats} seats)</div>
            </div>
          </div>
        )}

        <div className="net-stat-card">
          <div className="net-stat-card__icon net-stat-card__icon--amber">
            <Award size={22} />
          </div>
          <div>
            <div className="net-stat-card__val">{totalConnections * 10} pts</div>
            <div className="net-stat-card__lbl">Network Reward Tier</div>
          </div>
        </div>
      </div>

      {/* 3. TABS & CONTROLS TOOLBAR */}
      <div className="net-toolbar-card">
        <div className="net-toolbar-top">
          {/* Sub-Tabs Switcher */}
          <div className="net-tabs-list">
            <button
              type="button"
              className={`net-tab-btn ${activeTab === "partners" ? "net-tab-btn--active" : ""}`}
              onClick={() => setActiveTab("partners")}
            >
              <Users size={16} />
              <span>Network Partners</span>
              <span className="net-tab-badge">{partners.length}</span>
            </button>

            <button
              type="button"
              className={`net-tab-btn ${activeTab === "contacts" ? "net-tab-btn--active" : ""}`}
              onClick={() => setActiveTab("contacts")}
            >
              <UserCheck size={16} />
              <span>Customers & Friends</span>
              <span className="net-tab-badge">{contacts.length}</span>
            </button>

            {!isCustomer && (
              <button
                type="button"
                className={`net-tab-btn ${activeTab === "teams" ? "net-tab-btn--active" : ""}`}
                onClick={() => setActiveTab("teams")}
              >
                <Building size={16} />
                <span>Teams & Roster</span>
                <span className="net-tab-badge">{teams.length}</span>
              </button>
            )}
          </div>

          {/* Quick Action Button for current tab */}
          <div className="net-actions-group">
            {activeTab === "partners" && (
              <button
                type="button"
                className="net-btn net-btn--primary net-btn--sm"
                onClick={() => setShowAddPartnerModal(true)}
              >
                <UserPlus size={14} /> + Add Partner
              </button>
            )}
            {activeTab === "contacts" && (
              <button
                type="button"
                className="net-btn net-btn--primary net-btn--sm"
                style={{ background: "#16a34a" }}
                onClick={() => setShowAddContactModal(true)}
              >
                <UserCheck size={14} /> + Add Contact
              </button>
            )}
            {activeTab === "teams" && !isCustomer && (
              <button
                type="button"
                className="net-btn net-btn--primary net-btn--sm"
                style={{ background: "#9333ea" }}
                onClick={() => setShowCreateTeamModal(true)}
              >
                <Building size={14} /> + Create Team
              </button>
            )}
          </div>
        </div>

        {/* Search & Filters */}
        <div className="net-filter-bar">
          <div className="net-search-input-wrap">
            <Search size={16} className="net-search-icon" />
            <input
              type="text"
              className="net-search-input"
              placeholder={`Search ${activeTab === "partners" ? "partners by name, phone, city..." : activeTab === "contacts" ? "contacts by name, phone, area..." : "teams by name, description..."}`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="net-filter-controls">
            {activeTab === "partners" && (
              <select
                className="net-select-filter"
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
              >
                <option value="">All Locations</option>
                <option value="Mumbai">Mumbai</option>
                <option value="Maharashtra">Maharashtra</option>
                <option value="Delhi">Delhi</option>
                <option value="Bangalore">Bangalore</option>
                <option value="Pune">Pune</option>
                <option value="Hyderabad">Hyderabad</option>
              </select>
            )}

            <select
              className="net-select-filter"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="DESC">Newest First</option>
              <option value="ASC">Oldest First</option>
            </select>

            {searchQuery && (
              <button
                type="button"
                className="net-btn net-btn--secondary net-btn--sm"
                onClick={() => {
                  setSearchQuery("");
                  setSelectedState("");
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 4. ACTIVE TAB CONTENT */}
      {activeTab === "partners" && (
        <NetworkPartnersTab
          partners={filteredPartners}
          isLoading={isLoadingPartners}
          searchTerm={searchQuery}
          onOpenAddModal={() => setShowAddPartnerModal(true)}
          onViewDetails={(p) => {
            setSelectedPartnerForDrawer(p);
            setShowPartnerDrawer(true);
          }}
          onEditPartner={(p) => {
            setSelectedPartnerForEdit(p);
            setShowEditPartnerModal(true);
          }}
          onDeletePartner={handleDeletePartner}
        />
      )}

      {activeTab === "contacts" && (
        <ContactsFriendsTab
          contacts={filteredContacts}
          isLoading={isLoadingContacts}
          searchTerm={searchQuery}
          onOpenAddModal={() => setShowAddContactModal(true)}
          onViewDetails={(c) => {
            setSelectedContactForDrawer(c);
            setShowContactDrawer(true);
          }}
          onEditContact={(c) => {
            setSelectedContactForEdit(c);
            setShowEditContactModal(true);
          }}
          onDeleteContact={handleDeleteContact}
        />
      )}

      {activeTab === "teams" && !isCustomer && (
        <PartnerTeamsTab
          teams={filteredTeams}
          isLoading={isLoadingTeams}
          searchTerm={searchQuery}
          onOpenCreateModal={() => setShowCreateTeamModal(true)}
          onViewTeam={(t) => {
            setSelectedTeamForDrawer(t);
            setShowTeamDrawer(true);
          }}
          onAddMember={(t) => {
            setSelectedTeamForMemberAdd(t);
            setShowAddTeamMemberModal(true);
          }}
          onDeleteTeam={handleDeleteTeam}
        />
      )}

      {/* 5. MODALS & DRAWERS */}
      <AddPartnerModal
        show={showAddPartnerModal}
        onClose={() => setShowAddPartnerModal(false)}
        onSuccess={loadPartners}
      />

      <EditPartnerModal
        show={showEditPartnerModal}
        partner={selectedPartnerForEdit}
        onClose={() => {
          setShowEditPartnerModal(false);
          setSelectedPartnerForEdit(null);
        }}
        onSuccess={loadPartners}
      />

      <PartnerDetailsDrawer
        show={showPartnerDrawer}
        partnerId={selectedPartnerForDrawer?.id}
        initialPartner={selectedPartnerForDrawer}
        onClose={() => {
          setShowPartnerDrawer(false);
          setSelectedPartnerForDrawer(null);
        }}
        onEdit={(p) => {
          setSelectedPartnerForEdit(p);
          setShowEditPartnerModal(true);
        }}
        onDelete={handleDeletePartner}
      />

      <AddContactModal
        show={showAddContactModal}
        onClose={() => setShowAddContactModal(false)}
        onSuccess={loadContacts}
      />

      <EditContactModal
        show={showEditContactModal}
        contact={selectedContactForEdit}
        onClose={() => {
          setShowEditContactModal(false);
          setSelectedContactForEdit(null);
        }}
        onSuccess={loadContacts}
      />

      <ContactDetailsDrawer
        show={showContactDrawer}
        contact={selectedContactForDrawer}
        onClose={() => {
          setShowContactDrawer(false);
          setSelectedContactForDrawer(null);
        }}
        onEdit={(c) => {
          setSelectedContactForEdit(c);
          setShowEditContactModal(true);
        }}
        onDelete={handleDeleteContact}
      />

      <CreateTeamModal
        show={showCreateTeamModal}
        onClose={() => setShowCreateTeamModal(false)}
        onSuccess={loadTeams}
      />

      <TeamDetailsDrawer
        show={showTeamDrawer}
        team={selectedTeamForDrawer}
        onClose={() => {
          setShowTeamDrawer(false);
          setSelectedTeamForDrawer(null);
        }}
        onAddMember={(t) => {
          setSelectedTeamForMemberAdd(t);
          setShowAddTeamMemberModal(true);
        }}
        onTeamDeleted={loadTeams}
      />

      <AddTeamMemberModal
        show={showAddTeamMemberModal}
        team={selectedTeamForMemberAdd}
        onClose={() => {
          setShowAddTeamMemberModal(false);
          setSelectedTeamForMemberAdd(null);
        }}
        onSuccess={() => {
          loadTeams();
          // Also reload team drawer if open
          if (showTeamDrawer) {
            setSelectedTeamForDrawer((prev) => ({ ...prev }));
          }
        }}
      />
    </div>
  );
};

export default NetworkHub;
